import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React from 'react';
import { useCommonDataTableUiTranslation } from '../../hooks';
import { TableCellContent } from './TableCellContent';
import { TableLoadingBar } from './TableLoadingBar';
import { TableStatesContent } from './TableStatesContent';
export function TableContent({ loading, error, filteredRowCount, rows, columns, sortColumnKey, sortColumnDesc, onSortColumnChange, activeRowId, onRowClick, isRowSelectable, rowSelectionEnabled, selectedRowIds, onRowSelectionChange, onSelectAllRowsInCurrentPageClick, tableSx, checkboxColumnSx, hideHeader = false, }) {
    const { t } = useCommonDataTableUiTranslation();
    let allRowsChecked = false;
    let someRowsChecked = false;
    if (selectedRowIds && selectedRowIds.length > 0) {
        allRowsChecked = true;
        for (const row of rows) {
            if (selectedRowIds.includes(row.id)) {
                someRowsChecked = true;
            }
            else if (isRowSelectable ? isRowSelectable(row) : true) {
                allRowsChecked = false;
                if (someRowsChecked) {
                    break;
                }
            }
        }
    }
    const visuallyHiddenSx = {
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
    };
    return (React.createElement(Box, { sx: { position: 'relative' } },
        loading && rows.length > 0 && React.createElement(TableLoadingBar, null),
        React.createElement(Table, { sx: tableSx },
            React.createElement(TableHead, null,
                React.createElement(TableRow, { sx: Object.assign({ backgroundColor: themeColors.surface.secondary }, (hideHeader ? visuallyHiddenSx : {})) },
                    rowSelectionEnabled && (React.createElement(TableCell, { padding: 'checkbox', sx: mergeSx3({ width: 48 }, checkboxColumnSx === null || checkboxColumnSx === void 0 ? void 0 : checkboxColumnSx.tableCellSx, checkboxColumnSx === null || checkboxColumnSx === void 0 ? void 0 : checkboxColumnSx.tableCellHeadSx) },
                        React.createElement(Checkbox, { checked: allRowsChecked, indeterminate: !allRowsChecked && someRowsChecked, disabled: rows.length === 0, onChange: (event) => {
                                if (onSelectAllRowsInCurrentPageClick) {
                                    const isChecked = event.target.checked;
                                    const selectableRows = isRowSelectable && isChecked ? rows.filter((row) => isRowSelectable(row)) : rows;
                                    onSelectAllRowsInCurrentPageClick(selectableRows, isChecked);
                                }
                            }, inputProps: {
                                'aria-label': filteredRowCount != null && rows.length < filteredRowCount
                                    ? t('selectAllRowsInCurrentPage')
                                    : t('selectAllRows'),
                                'aria-checked': !allRowsChecked && someRowsChecked ? 'mixed' : undefined,
                            } }))),
                    columns.map((column, index) => (React.createElement(TableCell, { key: index, sx: mergeSx2(column.tableCellSx, column.tableCellHeadSx) }, column.sortKey ? (React.createElement(TableSortLabel, { active: column.sortKey === sortColumnKey, direction: sortColumnDesc ? 'desc' : 'asc', sx: {
                            '&:focus': {
                                color: themeColors.primary.base,
                            },
                            '&:hover': {
                                color: themeColors.primary.base,
                            },
                        }, onClick: onSortColumnChange
                            ? () => {
                                if (column.sortKey) {
                                    onSortColumnChange(column.sortKey, column.sortKey === sortColumnKey ? !sortColumnDesc : false);
                                }
                            }
                            : undefined },
                        column.headerLabel,
                        column.sortKey === sortColumnKey && (React.createElement(Box, { component: 'span', sx: visuallyHidden }, sortColumnDesc ? t('sortedDesc') : t('sortedAsc'))))) : (column.headerLabel)))))),
            React.createElement(TableBody, null, rows.map((row) => {
                var _a;
                return (React.createElement(TableRow, { key: row.id, tabIndex: onRowClick ? 0 : undefined, onKeyDown: onRowClick
                        ? (event) => {
                            if (event.key === 'Enter') {
                                onRowClick(row);
                            }
                            else if (event.key === 'Space') {
                                event.preventDefault();
                                onRowClick(row);
                            }
                        }
                        : undefined, onClick: onRowClick
                        ? () => {
                            onRowClick(row);
                        }
                        : undefined, sx: {
                        outline: activeRowId === row.id ? '1px solid ' + themeColors.primary.border : undefined,
                        '&:hover': onRowClick
                            ? {
                                backgroundColor: (theme) => theme.palette.action.hover,
                            }
                            : {},
                        '&:focus-visible': {
                            outline: (theme) => '1px solid ' + theme.palette.primary.light,
                        },
                    } },
                    rowSelectionEnabled && (React.createElement(TableCell, { padding: 'checkbox', sx: mergeSx3({ width: 48 }, checkboxColumnSx === null || checkboxColumnSx === void 0 ? void 0 : checkboxColumnSx.tableCellSx, checkboxColumnSx === null || checkboxColumnSx === void 0 ? void 0 : checkboxColumnSx.tableCellBodySx) },
                        React.createElement(Checkbox, { checked: (_a = selectedRowIds === null || selectedRowIds === void 0 ? void 0 : selectedRowIds.includes(row.id)) !== null && _a !== void 0 ? _a : false, disabled: isRowSelectable ? !isRowSelectable(row) : false, onClick: (event) => event.stopPropagation(), onChange: (event) => {
                                if (onRowSelectionChange) {
                                    onRowSelectionChange(row, event.target.checked);
                                }
                            }, inputProps: {
                                'aria-label': t('selectRow'),
                            } }))),
                    columns.map((column, index) => (React.createElement(TableCell, { key: index, "data-test": column.cellField ? column.cellField.toString() + '-' + 'column' : undefined, sx: mergeSx2(column.tableCellSx, column.tableCellBodySx) },
                        React.createElement(TableCellContent, { column: column, row: row }))))));
            }))),
        React.createElement(TableStatesContent, { rows: rows, loading: loading, error: error })));
}
function mergeSx2(sx1, sx2) {
    if (sx1 && sx2) {
        return [...(Array.isArray(sx1) ? sx1 : [sx1]), ...(Array.isArray(sx2) ? sx2 : [sx2])];
    }
    else if (sx1) {
        return sx1;
    }
    else if (sx2) {
        return sx2;
    }
    else {
        return undefined;
    }
}
function mergeSx3(sx1, sx2, sx3) {
    if (sx2 && sx3) {
        return [...(Array.isArray(sx1) ? sx1 : [sx1]), ...(Array.isArray(sx2) ? sx2 : [sx2]), ...(Array.isArray(sx3) ? sx3 : [sx3])];
    }
    else if (sx2) {
        return [...(Array.isArray(sx1) ? sx1 : [sx1]), ...(Array.isArray(sx2) ? sx2 : [sx2])];
    }
    else if (sx3) {
        return [...(Array.isArray(sx1) ? sx1 : [sx1]), ...(Array.isArray(sx3) ? sx3 : [sx3])];
    }
    else {
        return sx1;
    }
}
